import React, { useState, useRef, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LockOutlined, CheckCircleOutline } from "@mui/icons-material";
import { PublicServiceRequest, PublicServiceRequestFile } from "../models/models";
import { BudgetTypeCode, PaymentTermsCode, PriorityTypeCode, ServiceTypeCode } from "../../servicing/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import useOfferPresence from "../../../shared/notifications/hooks/useOfferPresence";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { dt } from "../../../shared/themes/redesignTokens";
import { ImagePreviewStrip, useGalleryImages } from "../../../shared/components/gallery";
import dayjs from "dayjs";

/* ─── Priority pill color map ─── */
const priorityPillColors: Record<number, { color: string; bg: string; border: string }> = {
  [PriorityTypeCode.urgent]: { color: "#FFFFFF", bg: "#C0392B", border: "#C0392B" },
  [PriorityTypeCode.high]: { color: "#C0392B", bg: "#F9EBEA", border: "#F5B7B1" },
  [PriorityTypeCode.low]: { color: "#27AE60", bg: "#EAFAF1", border: "#ABEBC6" },
};

/* ─── Image file extensions ─── */
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]);

const isImageFile = (file: PublicServiceRequestFile): boolean => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  return IMAGE_EXTENSIONS.has(ext);
};

/* ─── Machine spec row item ─── */
const SpecItem = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
    <Box
      sx={{
        fontSize: "10px",
        fontWeight: 500,
        fontFamily: dt.font,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: dt.textMuted,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: dt.fontMono,
        color: dt.textPrimary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        letterSpacing: "0.2px",
      }}
    >
      {value}
    </Box>
  </Box>
);

/* ─── Footer detail item ─── */
const FooterItem = ({
  icon,
  label,
  value,
  mono = false,
  valueColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
  mono?: boolean;
  valueColor?: string;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "10px",
        fontWeight: 500,
        fontFamily: dt.font,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: dt.textMuted,
      }}
    >
      {icon}
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: mono ? dt.fontMono : dt.font,
        color: valueColor || dt.textPrimary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Box>
  </Box>
);

interface ServiceRequestCardProps {
  serviceRequest: PublicServiceRequest;
  onViewDetails?: (serviceRequest: PublicServiceRequest) => void;
  onUnpublish?: (serviceRequest: PublicServiceRequest) => void;
  onMakeOffer?: (serviceRequest: PublicServiceRequest) => void;
  onBookmark?: (serviceRequest: PublicServiceRequest) => void;
  isStarred?: boolean;
}

const ServiceRequestCard = ({
  serviceRequest,
  onViewDetails,
  onUnpublish,
  onMakeOffer,
  onBookmark,
  isStarred
}: ServiceRequestCardProps) => {
  const { translate } = useTranslation(["serviceBoard", "common", "servicing", "serviceOffers"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const presenceCounts = useOfferPresence();
  const preparingCount = presenceCounts[serviceRequest.id] || 0;

  useRedesignSetup();

  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isDescClamped, setIsDescClamped] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = descRef.current;
    if (!el || isDescExpanded) return;
    // Double RAF ensures browser has fully laid out + painted the clamped text
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (descRef.current) {
          setIsDescClamped(descRef.current.scrollHeight > descRef.current.clientHeight + 1);
        }
      });
    });
  });

  const handleViewDetails = () => {
    onViewDetails?.(serviceRequest);
  };

  const handleUnpublish = () => {
    onUnpublish?.(serviceRequest);
  };

  const handleMakeOffer = () => {
    onMakeOffer?.(serviceRequest);
  };

  const handleBookmark = () => {
    onBookmark?.(serviceRequest);
  };

  const getPriorityLabel = () => {
    switch (serviceRequest.priorityTypeCode) {
      case PriorityTypeCode.urgent:
        return translate("urgent");
      case PriorityTypeCode.high:
        return translate("priorityHigh");
      case PriorityTypeCode.low:
        return translate("priorityLow");
      default:
        return "";
    }
  };

  const getServiceTypeLabel = () => {
    switch (serviceRequest.serviceTypeCode) {
      case ServiceTypeCode.onSiteRepair:
        return translate("serviceTypeRepair");
      case ServiceTypeCode.maintenance:
        return translate("serviceTypeMaintenance");
      case ServiceTypeCode.partsReplacement:
        return translate("serviceTypeParts");
      default:
        return "-";
    }
  };

  const getServiceDateLabel = () => {
    if (serviceRequest.flexibleDates) {
      return translate("serviceDateFlexible");
    }
    if (serviceRequest.serviceStartDate) {
      return dayjs(serviceRequest.serviceStartDate).format("DD MMM YYYY");
    }
    return translate("serviceDateAsap");
  };

  const isServiceDateAsap = () => {
    return !serviceRequest.flexibleDates && !serviceRequest.serviceStartDate;
  };

  const getBudgetLabel = () => {
    if (!serviceRequest.budgetTypeCode) return "\u2014";
    switch (serviceRequest.budgetTypeCode) {
      case BudgetTypeCode.fixed:
        return serviceRequest.fixedBudget
          ? `\u20AC${serviceRequest.fixedBudget.toLocaleString()}`
          : "\u2014";
      case BudgetTypeCode.range:
        if (serviceRequest.budgetRangeFrom && serviceRequest.budgetRangeTo) {
          return `\u20AC${serviceRequest.budgetRangeFrom.toLocaleString()}\u2014\u20AC${serviceRequest.budgetRangeTo.toLocaleString()}`;
        }
        return "\u2014";
      case BudgetTypeCode.requestQuote:
        return translate("budgetRequestQuote");
      default:
        return "\u2014";
    }
  };

  const getPaymentTermsLabel = () => {
    if (!serviceRequest.paymentTermsCode) return "\u2014";
    switch (serviceRequest.paymentTermsCode) {
      case PaymentTermsCode.net30Days:
        return translate("paymentNet30");
      case PaymentTermsCode.net60Days:
        return translate("paymentNet60");
      case PaymentTermsCode.net90Days:
        return translate("paymentNet90");
      case PaymentTermsCode.dueOnReceipt:
        return translate("paymentDueOnReceipt");
      case PaymentTermsCode.upfront50Completion:
        return translate("paymentUpfront50");
      default:
        return "\u2014";
    }
  };

  const getExpiresInLabel = () => {
    if (!serviceRequest.serviceEndDate) return null;
    const endDate = dayjs(serviceRequest.serviceEndDate);
    const today = dayjs().startOf("day");
    const daysLeft = endDate.diff(today, "day");

    if (daysLeft < 0) return translate("expired");
    if (daysLeft === 0) return translate("expiresToday");
    if (daysLeft === 1) return translate("expiresTomorrow");
    return translate("expiresInDays", { days: daysLeft });
  };

  const expiresLabel = getExpiresInLabel();
  const priorityPill = priorityPillColors[serviceRequest.priorityTypeCode];
  const machine = serviceRequest.machine;

  // Get image files for thumbnails
  const imageFiles = (serviceRequest.files || []).filter(isImageFile);
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  const buttonSx = {
    fontFamily: dt.font,
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: dt.radiusPill,
    padding: "6px 18px",
    color: dt.textSecondary,
    borderColor: dt.cardBorder,
    whiteSpace: "normal",
    textAlign: "center",
    minHeight: "32px",
    height: "auto",
    lineHeight: 1.3,
    "&:hover": {
      borderColor: dt.blue,
      color: dt.blue,
      backgroundColor: dt.blueSoft,
    },
  } as const;

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        backgroundColor: dt.cardBg,
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* ================================================================ */}
      {/*  LEFT COLUMN — header + content + footer                        */}
      {/* ================================================================ */}
      <Box
        sx={{
          flex: isPhone ? "none" : "7",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: isPhone ? "none" : `1px solid ${dt.cardBorder}`,
        }}
      >
        {/* ─── HEADER BAR — RFS number + service type + bookmark ─── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: { xs: "14px", sm: "20px" },
            py: "10px",
            borderBottom: `1px solid ${dt.cardBorder}`,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
            <Box
              component={RouterLink}
              to={`/service-board/${serviceRequest.id}`}
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "14px",
                fontWeight: 700,
                color: dt.blue,
                textDecoration: "none",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {serviceRequest.internalNumber}
            </Box>

            <Box sx={{ color: dt.textMuted, fontSize: "14px", fontWeight: 400, lineHeight: 1, userSelect: "none" }}>
              {"\u00B7"}
            </Box>

            <Box sx={{ fontSize: "12px", fontWeight: 500, fontFamily: dt.font, color: dt.textSecondary, whiteSpace: "nowrap" }}>
              {getServiceTypeLabel()}
            </Box>

            {serviceRequest.isDirectToMe && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  backgroundColor: "#E8F8F5",
                  color: "#27AE60",
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  borderRadius: "12px",
                  padding: "2px 8px",
                  whiteSpace: "nowrap",
                  lineHeight: "18px",
                }}
              >
                <LockOutlined sx={{ fontSize: "13px" }} />
                {translate("servicing:badge.direct")}
              </Box>
            )}

            {expiresLabel && (
              <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 600, color: "#C0392B", whiteSpace: "nowrap" }}>
                {expiresLabel}
              </Box>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={handleBookmark}
            sx={{
              color: isStarred ? "#F5A623" : dt.textMuted,
              p: "4px",
              flexShrink: 0,
              "&:hover": {
                color: isStarred ? "#E09100" : dt.textSecondary,
                backgroundColor: isStarred ? "#FFF8E1" : dt.inputBg,
              },
            }}
          >
            {isStarred ? <StarIcon sx={{ fontSize: "20px" }} /> : <StarBorderIcon sx={{ fontSize: "20px" }} />}
          </IconButton>
        </Box>

        {/* ─── CONTENT — company, title, description, thumbnails ─── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            px: { xs: "14px", sm: "20px" },
            py: "16px",
          }}
        >
          {/* Company avatar + name + priority pill + status pill + offers + own tag */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
            {serviceRequest.customer.logoUrl ? (
              <Box
                component="img"
                src={serviceRequest.customer.logoUrl}
                alt={serviceRequest.customer.name}
                sx={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${dt.cardBorder}` }}
              />
            ) : (
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: "50%", backgroundColor: dt.blueSoft,
                  border: `2px solid ${dt.blueGlow}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 700, color: dt.blue }}>
                  {serviceRequest.customer.name?.charAt(0)?.toUpperCase() || "?"}
                </Box>
              </Box>
            )}

            <Box
              component={RouterLink}
              to={`/companies/${serviceRequest.customer.id}`}
              sx={{
                fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.blue, textDecoration: "none",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, maxWidth: "200px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {serviceRequest.customer.name}
            </Box>

            {getPriorityLabel() && priorityPill && (
              <Box
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  backgroundColor: priorityPill.bg, color: priorityPill.color, border: `1px solid ${priorityPill.border}`,
                  borderRadius: "12px", padding: "2px 10px", fontSize: "11px", fontWeight: 600,
                  fontFamily: dt.font, whiteSpace: "nowrap", lineHeight: "18px",
                }}
              >
                {serviceRequest.priorityTypeCode === PriorityTypeCode.urgent && (
                  <WarningAmberIcon sx={{ fontSize: "13px" }} />
                )}
                {getPriorityLabel()}
              </Box>
            )}

            <Box
              sx={{
                display: "inline-flex", alignItems: "center", fontSize: "11px", fontWeight: 600,
                fontFamily: dt.font, color: "#27AE60", border: "1px solid #ABEBC6", borderRadius: "12px",
                padding: "2px 10px", whiteSpace: "nowrap", lineHeight: "18px", backgroundColor: "transparent",
              }}
            >
              {translate("openForOffers")}
            </Box>

            {serviceRequest.isOwnCompany && serviceRequest.offersCount > 0 && (
              <Box
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(`/service-requests/${serviceRequest.id}?tab=offers`);
                }}
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: dt.blue,
                  backgroundColor: dt.blueSoft,
                  padding: "2px 8px",
                  borderRadius: "10px",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: dt.blueGlow,
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 6px rgba(52,152,219,0.2)",
                  },
                }}
              >
                {translate("offersCount", { count: serviceRequest.offersCount })}
              </Box>
            )}

            {serviceRequest.hasMyOffer && !serviceRequest.isOwnCompany && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  backgroundColor: "#E8F8F5",
                  color: "#2AA198",
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  border: "1px solid #A3E4D7",
                  borderRadius: "12px",
                  padding: "2px 8px",
                  whiteSpace: "nowrap",
                  lineHeight: "18px",
                }}
              >
                <CheckCircleOutline sx={{ fontSize: "13px" }} />
                {translate("offerSent")}
              </Box>
            )}

            {preparingCount > 0 && (
              <Box
                sx={{
                  fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 600, color: "#D97706",
                  backgroundColor: "rgba(217, 119, 6, 0.08)", padding: "2px 8px", borderRadius: "10px",
                  whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "4px",
                  animation: "presencePulse 2s ease-in-out infinite",
                  "@keyframes presencePulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.6 } },
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#D97706" }} />
                {translate("serviceOffers:preparingOffers", { count: preparingCount })}
              </Box>
            )}

            {serviceRequest.isOwnCompany && (
              <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, color: dt.textSecondary, whiteSpace: "nowrap" }}>
                {translate("yourRequest")}
              </Box>
            )}
          </Box>

          {/* Subject/Issue — bold title */}
          <Box
            sx={{
              fontSize: "16px", fontWeight: 700, fontFamily: dt.font, color: dt.textPrimary,
              lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em",
            }}
          >
            <Tooltip title={serviceRequest.issue || ""} enterDelay={400} arrow>
              <span>{serviceRequest.issue || "\u2014"}</span>
            </Tooltip>
          </Box>

          {/* Description box */}
          {serviceRequest.description && (
            <Box>
              <Box
                ref={descRef}
                sx={{
                  backgroundColor: dt.inputBg, borderRadius: dt.radiusSm, padding: "12px 16px",
                  fontSize: "12px", fontFamily: dt.font, fontWeight: 400, lineHeight: 1.625, color: dt.textSecondary,
                  position: "relative",
                  ...(!isDescExpanded && {
                    maxHeight: "calc(3 * 1.625em + 24px)",
                    overflow: "hidden",
                  }),
                  ...(!isDescExpanded && isDescClamped && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "28px",
                      background: `linear-gradient(to bottom, transparent, ${dt.inputBg})`,
                      borderRadius: `0 0 ${dt.radiusSm} ${dt.radiusSm}`,
                      pointerEvents: "none",
                    },
                  }),
                }}
              >
                {serviceRequest.description}
              </Box>
              {(isDescClamped || isDescExpanded) && (
                <Box
                  component="span"
                  onClick={() => setIsDescExpanded((p) => !p)}
                  sx={{
                    display: "inline-block", marginTop: "4px", fontSize: "11px", fontFamily: dt.font,
                    fontWeight: 600, color: dt.blue, cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {isDescExpanded ? translate("common:showLess") : translate("common:showMore")}
                </Box>
              )}
            </Box>
          )}

          {/* Spacer pushes thumbnails to bottom */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Image thumbnails — click opens lightbox */}
          {galleryImages.length > 0 && (
            <ImagePreviewStrip images={galleryImages} maxVisible={3} />
          )}
        </Box>

        {/* ─── FOOTER BAR — Posted, Service Date, Payment Terms, Budget ─── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "12px", sm: "20px" },
            px: { xs: "14px", sm: "20px" },
            py: "10px",
            borderTop: `1px solid ${dt.cardBorder}`,
            backgroundColor: "#FAFBFC",
            flexWrap: "wrap",
          }}
        >
          <FooterItem
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: "11px" }} />}
            label={translate("serviceBoard:postedDate", { date: "" }).replace(/\s+$/, "")}
            value={serviceRequest.openDate ? dayjs(serviceRequest.openDate).format("MMM DD, YYYY") : "\u2014"}
            mono
          />
          <FooterItem
            icon={<CalendarTodayOutlinedIcon sx={{ fontSize: "11px" }} />}
            label={translate("detailServiceDate")}
            value={getServiceDateLabel()}
            mono={!isServiceDateAsap()}
            valueColor={isServiceDateAsap() ? dt.blue : undefined}
          />
          <Box sx={{ flex: 1 }} />
          <FooterItem
            icon={<PaymentOutlinedIcon sx={{ fontSize: "11px" }} />}
            label={translate("detailPaymentTerms")}
            value={getPaymentTermsLabel()}
          />
          <FooterItem
            icon={
              <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, fontFamily: dt.fontMono, color: dt.textMuted }}>
                {"\u20AC"}
              </Box>
            }
            label={translate("detailBudget")}
            value={getBudgetLabel()}
            mono
          />
        </Box>
      </Box>

      {/* ================================================================ */}
      {/*  RIGHT COLUMN — machine panel + action buttons (full height)    */}
      {/* ================================================================ */}
      <Box
        sx={{
          flex: isPhone ? "none" : "3",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#FAFBFC",
          borderTop: isPhone ? `1px solid ${dt.cardBorder}` : "none",
        }}
      >
        {/* Machine info */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            px: { xs: "14px", sm: "20px" },
            py: "18px",
            flex: 1,
          }}
        >
          {/* Machine image */}
          {(machine.imageUrl || machine.catalogImageUrl) ? (
            <Box
              component="img"
              src={machine.imageUrl || machine.catalogImageUrl}
              alt={machine.name}
              sx={{
                width: isPhone ? "80px" : "100%", maxWidth: "180px",
                height: isPhone ? "80px" : "auto", aspectRatio: isPhone ? "1" : "4/3",
                objectFit: "cover", borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`, backgroundColor: dt.inputBg,
              }}
            />
          ) : (
            <Box
              sx={{
                width: isPhone ? "80px" : "100%", maxWidth: "180px",
                height: isPhone ? "80px" : "auto", aspectRatio: isPhone ? "1" : "4/3",
                borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`, backgroundColor: dt.inputBg,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px",
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: "28px", color: dt.textMuted, opacity: 0.5 }} />
              <Box sx={{ fontSize: "10px", fontFamily: dt.font, fontWeight: 500, color: dt.textMuted }}>
                {translate("noImage")}
              </Box>
            </Box>
          )}

          {/* Machine name + category/year */}
          <Box sx={{ width: "100%", textAlign: "center", minWidth: 0 }}>
            <Tooltip title={machine.name || ""} enterDelay={400} arrow>
              <Box
                sx={{
                  fontSize: "14px", fontWeight: 700, fontFamily: dt.font, color: dt.textPrimary,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em",
                }}
              >
                {machine.name}
              </Box>
            </Tooltip>
            <Box
              sx={{
                fontSize: "11px", fontWeight: 400, fontFamily: dt.font, color: dt.textSecondary,
                mt: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {[machine.machineCategoryName, machine.productionYear].filter(Boolean).join(", ") || machine.model}
            </Box>
          </Box>

          {/* Machine spec grid */}
          <Box sx={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", mt: "4px" }}>
            <SpecItem label={translate("machineSerialNumber")} value={machine.serialNumber || "\u2014"} />
            <SpecItem label={translate("machineNumber")} value={machine.internalNumber ? `#${machine.internalNumber}` : "\u2014"} />
            <SpecItem label={translate("machineBrandLabel")} value={machine.brandName || "\u2014"} />
            <SpecItem label="Model" value={machine.model || "\u2014"} />
          </Box>

          {/* System */}
          <Box sx={{ width: "100%" }}>
            <SpecItem
              label={translate("machineSystem")}
              value={[machine.systemBrandName, machine.system].filter(Boolean).join(" ") || "\u2014"}
            />
          </Box>
        </Box>

        {/* Action buttons — bottom of right column */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
            px: { xs: "14px", sm: "16px" },
            py: "12px",
          }}
        >
          {serviceRequest.isOwnCompany ? (
            <Button variant="outlined" size="small" onClick={handleUnpublish} sx={buttonSx}>
              {translate("unpublishRequest")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={handleMakeOffer}
              sx={{
                ...buttonSx,
                ...(serviceRequest.hasMyOffer && {
                  color: "#2AA198",
                  borderColor: "#2AA198",
                  "&:hover": {
                    borderColor: "#239088",
                    color: "#239088",
                    backgroundColor: "rgba(42,161,152,0.06)",
                  },
                }),
              }}
            >
              {serviceRequest.hasMyOffer ? translate("sendAnotherOffer") : translate("makeOffer")}
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={handleViewDetails} sx={buttonSx}>
            {translate("viewDetails")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceRequestCard;
